'use client';

import { Box, HStack, VStack, token } from '@terra/ui-styled-system';
import {
  Tick01Icon,
  ArrowDown01Icon,
  ArrowRight01Icon,
  Clock01Icon,
  LeftToRightListBulletIcon,
  CheckListIcon,
  Loading03Icon,
  StopIcon,
  CancelCircleIcon,
  type TerraIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import { Badge } from '../../../components/badge';
import { Collapsible } from '../../../components/collapsible';
import { Text } from '../../../components/styled/text';
import { Tooltip } from '../../../components/tooltip';
import type { SkipAttackReasonType } from '../../traces/trace-types';
import { TracePlanItems, type TodoItem } from './TracePlan';
import { useTraceViewSettings } from './TraceViewSettingsContext';
import { formatDurationByMinutes } from './utils';

const TRACE_STATUS_ICON_SIZE = 10;

type TraceStatusIconProps = {
  Icon: TerraIcon;
  color?: string;
  ariaHidden?: boolean;
};

const getTraceStatusIconProps = ({
  isCompleted,
  isGuardrailBlocked,
  isAborted,
  isError,
}: {
  isCompleted: boolean;
  isGuardrailBlocked: boolean;
  isAborted: boolean;
  isError: boolean;
}): TraceStatusIconProps | null => {
  if (isCompleted) {
    return {
      Icon: Tick01Icon,
      color: token('colors.fg.status.resolved'),
    };
  }

  if (isGuardrailBlocked) {
    return {
      Icon: CancelCircleIcon,
      color: token('colors.fg.neutral.default'),
      ariaHidden: true,
    };
  }

  if (isAborted) {
    return {
      Icon: StopIcon,
      color: token('colors.fg.muted'),
      ariaHidden: true,
    };
  }

  if (isError) {
    return {
      Icon: CancelCircleIcon,
      color: token('colors.fg.status.unresolved'),
    };
  }

  return null;
};

export type TraceViewHeaderProps = {
  plan?: TodoItem[];
  summary?: string;
  status?: 'running' | 'completed' | 'error' | 'aborted';
  duration?: number;
  viewModeToggle?: React.ReactNode;
  searchInput?: React.ReactNode;
  executionLogToolbar?: React.ReactNode;
  variant?: 'detailed' | 'summary';
  skipAttackReasonType?: SkipAttackReasonType;
  disableDetailedView?: boolean;
};
export function TraceViewHeader({
  plan,
  status = 'completed',
  duration,
  viewModeToggle,
  searchInput,
  executionLogToolbar,
  variant = 'detailed',
  skipAttackReasonType,
  disableDetailedView,
}: TraceViewHeaderProps) {
  const t = useTranslations('designSystem.executions');
  const tViewMode = useTranslations(
    'designSystem.signals.drawer.traceView.viewMode',
  );
  const isSummaryView = variant === 'summary';
  const { settings, setIsPlanExpanded, setShowDetailedView } =
    useTraceViewSettings();
  const isPlanExpanded = settings.isPlanExpanded;
  const showDetailedView = disableDetailedView
    ? false
    : settings.showDetailedView;
  const [isPlanHeaderHovered, setIsPlanHeaderHovered] = useState(false);
  const handlePlanHeaderMouseEnter = () => {
    setIsPlanHeaderHovered(true);
  };

  const handlePlanHeaderMouseLeave = () => {
    setIsPlanHeaderHovered(false);
  };

  const completedCount = useMemo(
    () => plan?.filter((todo) => todo.status === 'completed').length || 0,
    [plan],
  );
  const totalCount = plan?.length || 0;
  const isRunning = status === 'running';
  const isError = status === 'error';
  const isAborted = status === 'aborted';
  const isCompleted = status === 'completed';
  const isGuardrailBlocked =
    isAborted && skipAttackReasonType === 'signal_blocked_by_guardrails';
  const traceStatusIconProps = isRunning
    ? undefined
    : getTraceStatusIconProps({
        isCompleted,
        isGuardrailBlocked,
        isAborted,
        isError,
      });
  const StatusIcon = traceStatusIconProps?.Icon;
  const statusIcon = StatusIcon ? (
    <StatusIcon
      size={TRACE_STATUS_ICON_SIZE}
      color={traceStatusIconProps?.color}
      aria-hidden={traceStatusIconProps?.ariaHidden || undefined}
    />
  ) : undefined;
  const statusSpinner = (
    <Box animation="spin 1s linear infinite" display="flex">
      <Loading03Icon size={TRACE_STATUS_ICON_SIZE} />
    </Box>
  );

  return (
    <VStack gap={3} alignItems="stretch" width="100%">
      {/* Title Row with Status and Detailed/Summary Toggle */}
      <HStack
        gap={2}
        alignItems="center"
        justifyContent="space-between"
        minH="32px"
      >
        <HStack gap={2} alignItems="center">
          <Text fontSize="sm" fontWeight="semibold">
            {isSummaryView ? t('traceSummary') : t('trace')}
          </Text>
          <Badge
            size="sm"
            variant="emphasis"
            w="fit-content"
            fontWeight="medium"
            icon={statusIcon}
            leadingContent={isRunning ? statusSpinner : undefined}
          >
            {isRunning
              ? t('status.running')
              : isCompleted
                ? t('status.completed')
                : isGuardrailBlocked
                  ? t('status.guardrail')
                  : isAborted
                    ? t('status.aborted')
                    : t('status.failed')}
          </Badge>
          {duration && (
            <HStack gap={1} alignItems="center" color="fg.muted">
              <Clock01Icon size={12} />
              <Text fontSize="xs">{formatDurationByMinutes(duration)}</Text>
            </HStack>
          )}
        </HStack>

        {/* Right side: Detailed/Summary Toggle */}
        <HStack gap={3} alignItems="center">
          {searchInput}
          {viewModeToggle}

          <HStack
            gap="0"
            borderWidth="1px"
            borderColor="border.default"
            borderRadius="md"
            overflow="hidden"
            flexShrink={0}
          >
            <Tooltip
              title={
                disableDetailedView
                  ? tViewMode('detailedUnavailableTooltip')
                  : tViewMode('detailedTooltip')
              }
            >
              <Box
                as="button"
                aria-label={tViewMode('detailed')}
                aria-disabled={disableDetailedView || undefined}
                onClick={() => {
                  if (disableDetailedView) return;
                  setShowDetailedView(true);
                }}
                h="7"
                px="2"
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap="1"
                cursor={disableDetailedView ? 'not-allowed' : 'pointer'}
                opacity={disableDetailedView ? 0.5 : undefined}
                bg={showDetailedView ? 'bg.muted' : 'transparent'}
                color={showDetailedView ? 'fg.default' : 'fg.muted'}
                _hover={
                  disableDetailedView
                    ? undefined
                    : {
                        bg: showDetailedView ? 'bg.muted' : 'bg.hover',
                      }
                }
              >
                <LeftToRightListBulletIcon size={12} />
                <Text fontSize="xs" fontWeight="medium">
                  {tViewMode('detailed')}
                </Text>
              </Box>
            </Tooltip>
            <Tooltip title={tViewMode('summaryTooltip')}>
              <Box
                as="button"
                aria-label={tViewMode('summary')}
                onClick={() => setShowDetailedView(false)}
                h="7"
                px="2"
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap="1"
                cursor="pointer"
                bg={!showDetailedView ? 'bg.muted' : 'transparent'}
                color={!showDetailedView ? 'fg.default' : 'fg.muted'}
                _hover={{
                  bg: !showDetailedView ? 'bg.muted' : 'bg.hover',
                }}
              >
                <LeftToRightListBulletIcon size={12} />
                <Text fontSize="xs" fontWeight="medium">
                  {tViewMode('summary')}
                </Text>
              </Box>
            </Tooltip>
          </HStack>
        </HStack>
      </HStack>

      {/* Plan Section - Collapsible */}
      {plan && plan.length > 0 && (
        <Collapsible.Root
          open={isPlanExpanded}
          onOpenChange={({ open }) => setIsPlanExpanded(open)}
        >
          <Box
            borderWidth="1px"
            borderColor="border.default"
            borderRadius="md"
            overflow="hidden"
            width="100%"
          >
            {/* Plan Header - Clickable */}
            <Collapsible.Trigger asChild>
              <HStack
                gap={2}
                px={3}
                py={2}
                bg="bg.subtle"
                justifyContent="space-between"
                cursor="pointer"
                _hover={{ bg: 'bg.muted' }}
                onMouseEnter={handlePlanHeaderMouseEnter}
                onMouseLeave={handlePlanHeaderMouseLeave}
              >
                <HStack gap={2} alignItems="center">
                  <Box color="fg.muted" display="flex" alignItems="center">
                    {isPlanHeaderHovered ? (
                      isPlanExpanded ? (
                        <ArrowDown01Icon size={14} />
                      ) : (
                        <ArrowRight01Icon size={14} />
                      )
                    ) : (
                      <CheckListIcon size={14} />
                    )}
                  </Box>
                  <Text fontSize="sm" fontWeight="medium" color="fg.default">
                    {t('plan')}
                  </Text>
                </HStack>
                <Text fontSize="xs" color="fg.muted">
                  {t('planProgress', {
                    completedCount,
                    totalCount,
                  })}
                </Text>
              </HStack>
            </Collapsible.Trigger>

            {/* Plan Steps - Collapsible */}
            <Collapsible.Content>
              <Box px={3}>
                <TracePlanItems plan={plan} status={status} />
              </Box>
            </Collapsible.Content>
          </Box>
        </Collapsible.Root>
      )}

      {/* Execution Log Section Header - only show in detailed view */}
      {!isSummaryView && (
        <Box borderTopWidth="2px" borderTopColor="border.default" pt={3}>
          <HStack
            gap={3}
            alignItems="center"
            justifyContent="space-between"
            minW={0}
          >
            <Text
              fontSize="sm"
              fontWeight="medium"
              color="fg.default"
              flexShrink={0}
            >
              {t('executionLog')}
            </Text>
            {executionLogToolbar ? (
              <Box flex={1} minW={0} display="flex" justifyContent="flex-end">
                {executionLogToolbar}
              </Box>
            ) : null}
          </HStack>
        </Box>
      )}
    </VStack>
  );
}
